import {
  ArrowBack,
  ChatBubbleOutlineOutlined,
  ErrorOutline,
  HourglassEmpty,
  MailOutline
} from "@mui/icons-material";
import {
  AppBar,
  Box,
  Dialog,
  IconButton,
  Slide,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { forwardRef, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useFetch } from "../../../hooks/Fetching";
import usePrimaryColor from "../../../hooks/usePrimaryColor";
import useUser from "../../../hooks/useUser";
import { isArrayEmpty, useIsSmallerScreen } from "../../../utils/Functions";
import Message from "../components/Message";
import { scrollType } from "../components/MessagesWindow";
import SendMessage from "../components/SendMessage";
import useMessageWebsocket from "../hooks/useMessageWebsocket";
import useMarkConvoAsRead from "../hooks/useMarkConvoAsRead";

const Transition = forwardRef((props, ref) => {
  return <Slide direction="left" ref={ref} {...props} />;
});

export default function MessagesWindowMobile({ convoId }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useUser();
  const hasConvoId = convoId != null && convoId !== undefined;
  const bottomRef = useRef(null);
  const isSmallScreen = useIsSmallerScreen();
  const shouldFetch = isSmallScreen;
  const convoKey = `conversations_dto${convoId}`;
  const messagesKey = `messages_for_convo${convoId}`;

  const {
    data: conversationDto,
    isLoading: loadingConvos,
    error: conversationError,
  } = useFetch("/inbox/conversation/", convoId, convoKey, shouldFetch);

  const { data, isLoading, error } = useFetch(
    "/inbox/conversation/messages/",
    convoId,
    messagesKey,
    shouldFetch
  );

  function removeParam() {
    searchParams.delete("convo");
    setSearchParams(searchParams);
  }

  function handleClose() {
    wasOpenRef.current = false;
    removeParam();
  }

  const dataEmpty = isArrayEmpty(data);
  const conversationDtoEmpty = isArrayEmpty(conversationDto);
  const isReceiver = Number(profile.id) === Number(conversationDto?.receiverId);

  const messagesToUpdate = useMessageWebsocket(convoId, !isReceiver);
  useMarkConvoAsRead(convoId, hasConvoId && shouldFetch && !isLoading && !error && data != null);

  const messages = useMemo(() => {
    return data?.map((msg, idx) => {
      const update = messagesToUpdate.includes(msg?.id);
      return <Message key={msg.id} msg={msg} idx={idx} update={update} />;
    });
  }, [data, messagesToUpdate]);

  const wasOpenRef = useRef(false);

  //Handles scrolling to bottom once the dialog is opened, executes on dialog open
  React.useEffect(() => {
    const isOpen = isSmallScreen && hasConvoId;

    if (isOpen && !wasOpenRef.current) {
      setTimeout(() => {
        bottomRef.current?.scrollIntoView({ behavior: scrollType });
      }, 100);
    }

    wasOpenRef.current = isOpen;
  }, [isSmallScreen, hasConvoId]);

  //Handles scrolling to bottom once a new message is sent
  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: scrollType });
  }, [data?.length]);

  if (isLoading || loadingConvos) {
    return (
      <Dialog
        open={isSmallScreen && hasConvoId}
        fullScreen
        TransitionComponent={Transition}
      >
        <Wrapper handleClose={handleClose}>
          <StatusMessage type="loading" title="LoadingConversation" />
        </Wrapper>
      </Dialog>
    );
  } else if (error || conversationError) {
    removeParam();
    return (
      <Dialog
        open={isSmallScreen && hasConvoId}
        fullScreen
        TransitionComponent={Transition}
      >
        <Wrapper handleClose={handleClose}>
          <StatusMessage
            type="error"
            title="SomethingWentWrong"
            subtitle="PleaseTryAgainLaterOrRefreshThePage"
          />
        </Wrapper>
      </Dialog>
    );
  } else if (dataEmpty || conversationDtoEmpty) {
    return (
      <Dialog
        open={isSmallScreen && hasConvoId}
        fullScreen
        TransitionComponent={Transition}
      >
        <Wrapper
          handleClose={handleClose}
          conversationDto={conversationDto}
          bottomRef={bottomRef}
        >
          <StatusMessage
            type="noData"
            title="NoMessagesYet"
            subtitle="StartTheConversation"
          />
        </Wrapper>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={isSmallScreen && hasConvoId}
      fullScreen
      TransitionComponent={Transition}
    >
      <Wrapper
        handleClose={handleClose}
        bottomRef={bottomRef}
        conversationDto={conversationDto}
      >
        {messages}
      </Wrapper>
    </Dialog>
  );
}

function Wrapper({ children, conversationDto, bottomRef, handleClose }) {
  const { t } = useTranslation();
  const isMasterSender = conversationDto?.senderEmail === "System Info";
  const isReceiverOrSenderDeleted=conversationDto?.deletedUsersInConversation;
  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.default",
      }}
    >
      <AppBar
        position="static"
        color="default"
        elevation={1}
        sx={{
          borderBottom: `1px solid`,
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Toolbar
          sx={{
            px: { xs: 1, sm: 2 },
            py: 1,
          }}
        >
          {handleClose ? (
            <IconButton
              edge="start"
              onClick={() => handleClose()}
              aria-label={t("Back")}
              title={t("Back")}
              sx={{ mr: 1 }}
            >
              <ArrowBack />
            </IconButton>
          ) : null}
          <Box
            sx={{
              textAlign: "left",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {conversationDto ? (
              <>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {t("To")}: {conversationDto?.receiverEmail}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                  {t("From")}: {conversationDto?.senderEmail}
                </Typography>
              </>
            ) : null}
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 1, sm: 2 },
          py: 2,
          backgroundColor: "background.default",
        }}
      >
        {children}
        <div ref={bottomRef} />
      </Box>
      <Box>
        <SendMessage
          isMasterSender={isMasterSender}
          scrollToBottom={() =>
            bottomRef.current?.scrollIntoView({ behavior: scrollType })
          }
          isReceiverOrSenderDeleted={isReceiverOrSenderDeleted}
        />
      </Box>
    </Box>
  );
}

function StatusMessage({ type = "noData", title, subtitle }) {
  const { t } = useTranslation();
  const color = usePrimaryColor();

  const icons = {
    loading: <HourglassEmpty color="disabled" fontSize="large" />,
    error: <ErrorOutline color="error" fontSize="large" />,
    noData: <MailOutline sx={{ color: color }} fontSize="large" />,
    noConvo: (
      <ChatBubbleOutlineOutlined sx={{ color: color }} fontSize="large" />
    ),
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
      }}
    >
      <Stack
        spacing={2}
        alignItems="center"
        justifyContent="center"
        sx={{ px: 2 }}
      >
        {icons[type]}
        <Typography variant="h6" align="center" color="text.secondary">
          {t(title)}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" align="center">
            {t(subtitle)}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
